// Base URL Manager for Subfolder Support
// Handles dynamic base URL configuration for PWA deployment in subfolders

class BaseUrlManager {
  private baseUrl: string = '';
  private basePath: string = '/';

  constructor() {
    this.initialize();
  }

  // Initialize base URL from various sources
  private initialize() {
    // Try to get from localStorage first
    const storedBaseUrl = localStorage.getItem('app_base_url');
    if (storedBaseUrl) {
      this.baseUrl = storedBaseUrl;
      this.basePath = new URL(storedBaseUrl).pathname;
      return;
    }

    // Try to get from global variables
    if ((window as any).APP_BASE_URL) {
      this.baseUrl = (window as any).APP_BASE_URL;
      this.basePath = (window as any).APP_BASE_PATH || '/';
      return;
    }

    // Auto-detect from current URL
    this.autoDetectBaseUrl();
  }

  // Auto-detect base URL from current location
  private autoDetectBaseUrl() {
    const currentUrl = window.location.href;
    const currentPath = window.location.pathname;
    const currentOrigin = window.location.origin;
    
    // Extract base path (everything before the last segment)
    const pathSegments = currentPath.split('/').filter(segment => segment);
    let basePath = '/';
    
    if (pathSegments.length > 0) {
      const lastSegment = pathSegments[pathSegments.length - 1];
      if (!lastSegment.includes('.') && lastSegment !== 'index.html') {
        basePath = '/' + pathSegments.join('/') + '/';
      } else if (pathSegments.length > 1) {
        basePath = '/' + pathSegments.slice(0, -1).join('/') + '/';
      }
    }
    
    this.baseUrl = currentOrigin + basePath;
    this.basePath = basePath;
    
    // Store for future use
    this.saveToStorage();
  }

  // Save base URL to localStorage
  private saveToStorage() {
    localStorage.setItem('app_base_url', this.baseUrl);
    localStorage.setItem('app_base_path', this.basePath);
  }

  // Set base URL manually
  public setBaseUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      
      // Validate URL
      if (urlObj.protocol !== 'https:' && urlObj.hostname !== 'localhost') {
        console.error('Base URL must be HTTPS or localhost');
        return false;
      }

      if (!url.endsWith('/')) {
        console.error('Base URL should end with /');
        return false;
      }

      this.baseUrl = url;
      this.basePath = urlObj.pathname;
      
      // Update global variables
      (window as any).APP_BASE_URL = this.baseUrl;
      (window as any).APP_BASE_PATH = this.basePath;
      
      // Save to storage
      this.saveToStorage();
      
      // Update manifest if available
      if ((window as any).updateManifest) {
        (window as any).updateManifest();
      }
      
      console.log('Base URL updated:', { baseUrl: this.baseUrl, basePath: this.basePath });
      return true;
    } catch (error) {
      console.error('Invalid base URL:', error);
      return false;
    }
  }

  // Get current base URL
  public getBaseUrl(): string {
    return this.baseUrl;
  }

  // Get current base path
  public getBasePath(): string {
    return this.basePath;
  }

  // Get full URL for a given path
  public getFullUrl(path: string = ''): string {
    // Remove leading slash if present
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return this.baseUrl + cleanPath;
  }

  // Get relative path from base URL
  public getRelativePath(fullUrl: string): string {
    try {
      const urlObj = new URL(fullUrl);
      const baseUrlObj = new URL(this.baseUrl);
      
      if (urlObj.origin !== baseUrlObj.origin) {
        return fullUrl; // Return full URL if different origin
      }
      
      return urlObj.pathname;
    } catch (error) {
      console.error('Error getting relative path:', error);
      return fullUrl;
    }
  }

  // Check if URL is within base URL scope
  public isWithinScope(url: string): boolean {
    try {
      const urlObj = new URL(url);
      const baseUrlObj = new URL(this.baseUrl);
      
      return urlObj.origin === baseUrlObj.origin && 
             urlObj.pathname.startsWith(this.basePath);
    } catch (error) {
      return false;
    }
  }

  // Get URL info for debugging
  public getUrlInfo() {
    return {
      currentUrl: window.location.href,
      currentPath: window.location.pathname,
      currentOrigin: window.location.origin,
      baseUrl: this.baseUrl,
      basePath: this.basePath,
      isWithinScope: this.isWithinScope(window.location.href)
    };
  }

  // Reset to auto-detected values
  public reset() {
    this.autoDetectBaseUrl();
  }

  // Validate current configuration
  public validate(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!this.baseUrl) {
      errors.push('Base URL is not set');
    }
    
    if (!this.basePath) {
      errors.push('Base path is not set');
    }
    
    try {
      const urlObj = new URL(this.baseUrl);
      
      if (urlObj.protocol !== 'https:' && urlObj.hostname !== 'localhost') {
        errors.push('Base URL must be HTTPS or localhost');
      }
      
      if (!this.baseUrl.endsWith('/')) {
        errors.push('Base URL should end with /');
      }
    } catch (error) {
      errors.push('Base URL is invalid');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Create singleton instance
const baseUrlManager = new BaseUrlManager();

// Export the instance
export default baseUrlManager;

// Also export the class for testing
export { BaseUrlManager };
